import { ArrowLeftOutlined, CheckCircleFilled, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Card, Form, Input, Modal, Space, Switch } from 'antd';
import Layout from 'components/Layout';
import withGuard from 'components/withGuard';
import { PERMISSIONS } from 'constants/permissions';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router';
import { captureException } from 'utils/errors';
import { getData } from 'utils';
import {
  createFlow,
  createFlowNode,
  deleteFlowNode,
  getFlow,
  updateFlow,
  updateFlowNode,
} from 'api/flows';
import { StyledSuccessContent } from './styles';

const NODE_TYPE_ACTION = 'action';
const ACTION_TYPE_WEBHOOK = 'webhook';

function FlowBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isEdit = Boolean(id);
  const duplicateFromId = location.state?.duplicateFromId;
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [flow, setFlow] = useState(null);
  const [webhookSteps, setWebhookSteps] = useState([]);
  const [successModalOpen, setSuccessModalOpen] = useState(false);

  const loadFlow = useCallback(() => {
    if (!id) return;
    setLoading(true);
    getFlow(id)
      .then((res) => {
        const data = getData(res)?.data ?? getData(res);
        setFlow(data);
        form.setFieldsValue({
          name: data?.name ?? '',
          status: data?.status === 'active',
        });
        const nodes = data?.nodes ?? [];
        const actions = nodes
          .filter((n) => n.node_type === NODE_TYPE_ACTION && n.action_type === ACTION_TYPE_WEBHOOK)
          .sort((a, b) => a.position_index - b.position_index);
        setWebhookSteps(
          actions.map((n) => ({
            id: n.id,
            url: n.config_json?.url ?? '',
          })),
        );
        setLoading(false);
      })
      .catch((e) => {
        captureException(e);
        setLoading(false);
      });
  }, [id, form]);

  useEffect(() => {
    if (duplicateFromId && !isEdit) {
      setLoading(true);
      getFlow(duplicateFromId)
        .then((res) => {
          const data = getData(res)?.data ?? getData(res);
          form.setFieldsValue({
            name: `${data?.name ?? ''} (Copy)`,
            status: false,
          });
          const nodes = data?.nodes ?? [];
          const actions = nodes
            .filter((n) => n.node_type === NODE_TYPE_ACTION && n.action_type === ACTION_TYPE_WEBHOOK)
            .sort((a, b) => a.position_index - b.position_index);
          setWebhookSteps(actions.map((n) => ({ id: null, url: n.config_json?.url ?? '' })));
          setLoading(false);
        })
        .catch((e) => {
          captureException(e);
          setLoading(false);
        });
    } else if (isEdit) {
      loadFlow();
    }
  }, [loadFlow, duplicateFromId, isEdit]);

  const handleBack = () => {
    navigate('/console/marketing/flows');
  };

  const addStep = () => {
    setWebhookSteps((prev) => [...prev, { id: null, url: '' }]);
  };

  const removeStep = (index) => {
    setWebhookSteps((prev) => {
      const next = [...prev];
      next.splice(index, 1);
      return next;
    });
  };

  const updateStepUrl = (index, url) => {
    setWebhookSteps((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], url };
      return next;
    });
  };

  const moveStep = (index, direction) => {
    setWebhookSteps((prev) => {
      const next = [...prev];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const handleSave = () => {
    form.validateFields().then((values) => {
      setSaving(true);
      const name = values.name;
      const status = values.status ? 'active' : 'inactive';

      const saveFlow = isEdit
        ? updateFlow(id, { name, status })
        : createFlow({ name, status });

      saveFlow
        .then((res) => {
          const data = getData(res)?.data ?? getData(res);
          const flowId = data?.id ?? id;
          if (!isEdit) {
            setFlow(data);
          }

          const stepsToCreate = webhookSteps.filter((s) => !s.id && s.url);
          const stepsToUpdate = webhookSteps.filter((s) => s.id && s.url);
          const stepsToDelete = flow?.nodes
            ?.filter((n) => n.node_type === NODE_TYPE_ACTION && n.action_type === ACTION_TYPE_WEBHOOK)
            .filter((n) => !webhookSteps.some((s) => s.id === n.id))
            ?? [];

          const promises = [];

          stepsToDelete.forEach((node) => {
            promises.push(deleteFlowNode(flowId, node.id));
          });

          stepsToUpdate.forEach((step, idx) => {
            promises.push(
              updateFlowNode(flowId, step.id, {
                config_json: { url: step.url, method: 'POST' },
                position_index: idx + 1,
              }),
            );
          });

          stepsToCreate.forEach((step, idx) => {
            const baseIndex = stepsToUpdate.length;
            promises.push(
              createFlowNode(flowId, {
                node_type: NODE_TYPE_ACTION,
                action_type: ACTION_TYPE_WEBHOOK,
                config_json: { url: step.url, method: 'POST' },
                position_index: baseIndex + idx + 1,
              }),
            );
          });

          return Promise.all(promises).then(() => {
            setSaving(false);
            if (!isEdit) {
              setFlow(data);
            } else {
              loadFlow();
            }
            setSuccessModalOpen(true);
          });
        })
        .catch((e) => {
          captureException(e);
          setSaving(false);
        });
    });
  };

  if (loading && isEdit) {
    return (
      <Layout header={<Layout.Header title="Flow Builder" />}>
        <div style={{ padding: 48, textAlign: 'center' }}>Loading...</div>
      </Layout>
    );
  }

  return (
    <Layout
      header={(
        <Layout.Header
          title={isEdit ? 'Edit Flow' : 'Create Flow'}
          extra={[
            <Button key="back" icon={<ArrowLeftOutlined />} onClick={handleBack}>
              Back
            </Button>,
            <Button key="save" type="primary" loading={saving} onClick={handleSave}>
              Save
            </Button>,
          ]}
        />
      )}
    >
      <div style={{ maxWidth: 640, padding: 24 }}>
        <Card title="Flow Details" style={{ marginBottom: 24 }}>
          <Form form={form} layout="vertical">
            <Form.Item
              name="name"
              label="Name"
              rules={[{ required: true, message: 'Name is required' }]}
            >
              <Input placeholder="e.g. Push to Zapier" />
            </Form.Item>
            <Form.Item
              name="status"
              label="Status"
              valuePropName="checked"
              initialValue={false}
            >
              <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
            </Form.Item>
          </Form>
        </Card>

        <Card title="Flow Canvas">
          <div style={{ marginBottom: 16 }}>
            <div
              style={{
                padding: 12,
                background: '#f5f5f5',
                borderRadius: 8,
                textAlign: 'center',
                fontWeight: 500,
              }}
            >
              Manual Trigger
            </div>
            <div style={{ textAlign: 'center', padding: 8 }}>↓</div>
          </div>

          {webhookSteps.map((step, index) => (
            <div key={step.id ?? `new-${index}`} style={{ marginBottom: 16 }}>
              <div
                style={{
                  display: 'flex',
                  gap: 8,
                  alignItems: 'flex-start',
                }}
              >
                <Input
                  placeholder="Webhook URL"
                  value={step.url}
                  onChange={(e) => updateStepUrl(index, e.target.value)}
                  style={{ flex: 1 }}
                />
                <Button
                  icon={<DeleteOutlined />}
                  danger
                  onClick={() => removeStep(index)}
                />
                <Button
                  size="small"
                  disabled={index === 0}
                  onClick={() => moveStep(index, -1)}
                >
                  ↑
                </Button>
                <Button
                  size="small"
                  disabled={index === webhookSteps.length - 1}
                  onClick={() => moveStep(index, 1)}
                >
                  ↓
                </Button>
              </div>
              {index < webhookSteps.length - 1 && (
                <div style={{ textAlign: 'center', padding: 4 }}>↓</div>
              )}
            </div>
          ))}

          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={addStep}
            block
            style={{ marginTop: 8 }}
          >
            Add Webhook
          </Button>
        </Card>
      </div>

      <Modal
        open={successModalOpen}
        onCancel={() => setSuccessModalOpen(false)}
        footer={null}
        closable
      >
        <StyledSuccessContent>
          <div className="flow-success-checkmark">
            <CheckCircleFilled />
          </div>
          <h3>Flow saved successfully</h3>
          <p>What would you like to do next?</p>
          <Space>
            <Button
              type="primary"
              onClick={() => {
                setSuccessModalOpen(false);
                if (!isEdit) {
                  navigate(`/console/marketing/flows/edit/${flow?.id ?? id}`);
                }
              }}
            >
              Keep Editing
            </Button>
            <Button
              onClick={() => {
                setSuccessModalOpen(false);
                navigate('/console/marketing/flows');
              }}
            >
              View All Flows
            </Button>
          </Space>
        </StyledSuccessContent>
      </Modal>
    </Layout>
  );
}

export default withGuard(FlowBuilder, [PERMISSIONS.MARKETING], { redirect: true });
